<template>
  <div>
    <!-- Label -->
    <label :class="labelComputedClasses" :for="id">
      <slot name="label">
        <span>{{ label }}</span>
        <ips-tooltip v-if="tooltipProps" v-bind="tooltipProps">
          <template slot="tip">
            <ips-icon icon="ico-help"></ips-icon>
          </template>
        </ips-tooltip>
      </slot>
    </label>

    <div :class="formComputedClasses">
      <!-- Icon Prepend -->
      <ips-icon v-if="icon" :icon="icon" class="ips-form-icon--prepend"></ips-icon>

      <!-- Search field Icon -->
      <ips-icon v-if="type === 'search'" icon="ico-search" class="ips-form-icon--search"></ips-icon>

      <!-- Input Field -->
      <input
        :id="id"
        :type="inputType"
        class="form-control"
        :class="inputComputedClasses"
        :value="value"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder"
        :tabindex="tabindex"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        @input="$emit('input', $event.target.value)"
        @keyup.enter="enterPressed"
      />

      <!-- Icon Append -->
      <ips-icon
        v-if="iconAppend"
        :icon="iconAppend"
        class="ips-form-icon--append"
        @click="appendIconClicked"
      ></ips-icon>

      <!-- Clear Icon -->
      <ips-icon
        v-show="clearable && isDirty"
        icon="ico-delete"
        class="ips-form-icon--clear"
        @click="clearInputValue"
      ></ips-icon>

      <!-- Password Display Icon -->
      <ips-tooltip
        v-if="passwordReveal && isDirty"
        class="ips-form-icon--password"
        :tooltip="showPassword ? 'Hide password' : 'Show password'"
        placement="top"
      >
        <template slot="tip">
          <ips-icon
            :icon="showPassword ? 'ico-eye-off' : 'ico-eye'"
            @click="togglePassword"
          ></ips-icon>
        </template>
      </ips-tooltip>

      <!-- Validation Display -->
      <small v-if="error" class="ips-validation form-text text-danger">
        <ips-icon icon="ico-danger" size="sm"></ips-icon>
        <!-- @slot Use this slot to populate a validation/error message -->
        <slot name="errorText"></slot>
      </small>
    </div>
  </div>
</template>

<script>
import IpsIcon from '@ipscape/ips-icon';
import IpsTooltip from '@ipscape/ips-tooltip';
import { formFieldsMixin } from '../../../mixins/index.js';

export default {
  name: 'IpsInput',
  components: {
    IpsIcon,
    IpsTooltip,
  },
  mixins: [formFieldsMixin],
  props: {
    type: {
      name: 'type',
      description: 'The input type',
      type: String,
      default: 'text',
      required: false,
      validator: (value) =>
        ['', 'text', 'password', 'number', 'search', 'email'].indexOf(value) !== -1,
    },
    passwordReveal: {
      name: 'passwordReveal',
      description: 'Adds toggle view icon to password field.',
      type: Boolean,
      default: false,
      required: false,
    },
    tooltipProps: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      showPassword: false,
    };
  },
  computed: {
    isSearchType() {
      return this.type === 'search';
    },
    inputType() {
      if (this.type === 'password' && this.showPassword) {
        return 'text';
      }
      return this.type;
    },
  },
  methods: {
    clearInputValue() {
      if (this.disabled) return;
      this.$emit('input', '');
      this.$emit('cleared');
    },
    togglePassword() {
      if (this.disabled) return;
      this.showPassword = !this.showPassword;
    },
    enterPressed() {
      this.$emit('enter-pressed');
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'IpsInput';

input:focus::placeholder {
  color: transparent;
}
.ips-tooltip.ips-form-icon--password {
  position: absolute;
  top: 0.5rem;
  right: 12px;
}
</style>
